import { Check, X } from "lucide-react";
import { useState } from "react";

import { friendlyError } from "../lib/friendlyError";
import type { Registration } from "../pages/event-registrations/shared";
import { trpc } from "../trpc";

/**
 * The pieces a booking form shares between Reception and the Events
 * guest list: field and button styles, the adults/children stepper, and
 * the editor that changes a booking's name, contact details or party.
 */

/** The backend takes up to 50 adults and 50 children on one booking. */
const STEPPER_MAX = 50;

export const FIELD_LABEL =
  "flex min-w-0 flex-col gap-1.5 text-[15px] font-medium text-[color:var(--color-ink)]";
export const FIELD =
  "min-h-12 w-full rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] px-3.5 text-[16px] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-amber)]";
export const PRIMARY_BUTTON =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 text-[16px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-50";
export const SECONDARY_BUTTON =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-5 text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] disabled:opacity-50";

/** A labelled − value + stepper with 44px buttons. Shared by the walk-in
 *  form and the booking editor. Clamps between `min` (default 0) and the
 *  most the backend accepts. */
export function NumberStepper({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
}) {
  const noun = label.toLowerCase();
  return (
    <div
      role="group"
      aria-label={label}
      className="flex items-center gap-3 text-[15px] font-medium text-[color:var(--color-ink)]"
    >
      <span>{label}</span>
      <span className="inline-flex items-center rounded-full border border-[color:var(--color-line-strong)]">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          aria-label={`Fewer ${noun}`}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[20px] hover:text-[color:var(--color-amber)] disabled:opacity-40"
        >
          −
        </button>
        <span
          aria-live="polite"
          className="w-8 text-center text-[17px] font-semibold tabular-nums"
        >
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(Math.min(STEPPER_MAX, value + 1))}
          disabled={value >= STEPPER_MAX}
          aria-label={`More ${noun}`}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[20px] hover:text-[color:var(--color-amber)] disabled:opacity-40"
        >
          +
        </button>
      </span>
    </div>
  );
}

/**
 * Change a booking at the door: the name, contact details, or the party
 * when more or fewer people turn up than booked. totalPeople is worked
 * out on the server from adults + children.
 */
export function RegistrationEditor({
  registration,
  onClose,
  onSaved,
  intro = "Change their name, contact details or how many people came.",
}: {
  registration: Registration;
  onClose: () => void;
  onSaved: () => void;
  /** One plain line above the fields saying what can be changed. */
  intro?: string;
}) {
  const [name, setName] = useState(registration.name);
  const [email, setEmail] = useState(registration.email);
  const [phone, setPhone] = useState(registration.phone ?? "");
  const [adults, setAdults] = useState(registration.adults);
  const [children, setChildren] = useState(registration.children);
  const [error, setError] = useState<string | null>(null);

  const update = trpc.adminUpdateRegistration.useMutation();

  const save = () => {
    setError(null);
    if (!name.trim()) {
      setError("Please enter their name.");
      return;
    }
    if (adults + children < 1) {
      setError("Add at least one person.");
      return;
    }
    update.mutate(
      {
        id: registration.id,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() ? phone.trim() : null,
        adults,
        children,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setError(friendlyError(err)),
      },
    );
  };

  return (
    <div className="border-t border-[color:var(--color-line)] bg-[color:var(--color-paper)] px-4 py-4">
      <p className="mb-3 text-[15px] text-[color:var(--color-ink-soft)]">
        {intro}
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className={FIELD_LABEL}>
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className={FIELD}
          />
        </label>
        <label className={FIELD_LABEL}>
          Email (optional)
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className={FIELD}
          />
        </label>
        <label className={FIELD_LABEL}>
          Phone (optional)
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className={FIELD}
          />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
        <NumberStepper label="Adults" value={adults} onChange={setAdults} />
        <NumberStepper
          label="Children"
          value={children}
          onChange={setChildren}
        />
      </div>
      {error ? (
        <p role="alert" className="mt-3 text-[15px] font-medium text-red-700">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          onClick={save}
          disabled={update.isPending}
          className={`${PRIMARY_BUTTON} bg-[color:var(--color-ink)] text-[15px] text-[color:var(--color-paper)]`}
        >
          <Check size={16} aria-hidden="true" />
          {update.isPending ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={update.isPending}
          className={SECONDARY_BUTTON}
        >
          <X size={16} aria-hidden="true" />
          Cancel
        </button>
      </div>
    </div>
  );
}
